import React, { useState, useEffect } from "react";
import { Bell, LogOut } from "lucide-react";
import { getAuthService, getFirestoreService } from "../firebase/services";
import { useNavigate, Link } from "react-router-dom";
import rfaLogo from "../assets/rfa logo.webp";
import { titleCase } from "../utils/stringUtils";

const ADMIN_EMAIL = "reflectionfutbolacademy@gmail.com";

const DashboardNavbar = () => {
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState({
    name: "Player",
    initials: "P",
    profileImageUrl: "",
  });

  const getInitials = (name) => {
    if (!name) return "P";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  useEffect(() => {
    let unsubAuth = null;
    let cancelled = false;

    const init = async () => {
      const auth = await getAuthService();
      const db = await getFirestoreService();
      const { onAuthStateChanged } = await import("firebase/auth");
      const { doc, getDoc } = await import("firebase/firestore");
      if (cancelled) return;

      unsubAuth = onAuthStateChanged(auth, async (user) => {
        setCurrentUser(user);

        if (!user) {
          setUserData({ name: "Player", initials: "P", profileImageUrl: "" });
          return;
        }

        try {
          const userRef = doc(db, "users", user.uid);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            const fullName = userDoc.data().fullName || user.displayName || "Player";
            const profileImageUrl = userDoc.data().profileImageUrl || user.photoURL || "";
            setUserData({
              name: fullName,
              initials: getInitials(fullName),
              profileImageUrl,
            });
            return;
          }

          const fallbackName = user.displayName || "Player";
          setUserData({
            name: fallbackName,
            initials: getInitials(fallbackName),
            profileImageUrl: user.photoURL || "",
          });
        } catch (error) {
          console.error("Error loading user data:", error);
        }
      });
    };

    init();

    return () => {
      cancelled = true;
      if (unsubAuth) unsubAuth();
    };
  }, []);

  const handleLogout = async () => {
    try {
      const auth = await getAuthService();
      const { signOut } = await import("firebase/auth");
      await signOut(auth);
      navigate("/login");
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  return (
    <nav className="h-20 bg-white/10 backdrop-blur-md border-b border-white/10 fixed top-0 right-0 left-0 z-50 flex items-center justify-between px-4 md:px-8 transition-all">
      {/* Logo for Mobile/Tablet */}
      <Link to="/dashboard" className="flex items-center gap-2">
        <img
          src={rfaLogo}
          alt="RFA"
          width={40}
          height={40}
          className="h-10 w-auto"
        />
        <span className="hidden sm:block font-inter font-black text-white text-xs tracking-widest uppercase">
          RF Academy
        </span>
      </Link>

      {/* User Actions */}
      <div className="flex items-center gap-4">
        <button className="relative p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-colors">
          <Bell size={20} />
          <span className="absolute top-2 right-2 w-2 h-2 bg-[#F4B400] rounded-full border-2 border-[#7D192A]"></span>
        </button>

        <div className="h-8 w-[1px] bg-white/10 hidden sm:block"></div>

        <div className="flex items-center gap-3 group cursor-pointer">
          <div className="text-right hidden lg:block">
            {/* Display Dynamic Name */}
            <p className="text-xs font-black text-white uppercase leading-none">
              {titleCase(userData.name)}
            </p>
            <p className="text-[10px] text-[#F4B400] font-bold uppercase tracking-tighter">
              Member
            </p>
          </div>

          {/* Display Dynamic Initials */}
          <div className="w-10 h-10 bg-[#7D192A] rounded-full border-2 border-[#F4B400] flex items-center justify-center text-white font-bold shadow-lg group-hover:scale-105 transition-transform overflow-hidden">
            {userData.profileImageUrl ? (
              <img
                src={userData.profileImageUrl}
                alt={`${titleCase(userData.name)} profile`}
                className="w-full h-full object-cover rounded-full"
              />
            ) : (
              userData.initials
            )}
          </div>
        </div>

        <button
          onClick={handleLogout}
          className="p-2 text-white/70 hover:text-red-400 transition-colors"
          title="Logout"
        >
          <LogOut size={20} />
        </button>
      </div>
    </nav>
  );
};

export default DashboardNavbar;
